import { inject, Injectable, signal } from '@angular/core';

import { SDK, STORE } from '@plugins/middleware-sdk';
import { Override } from '@plugins/shared/types';
import { isObject } from '@plugins/shared/utils/assertFunctions';

import { IncomingTranslation, UnflattenedTranslationType } from './types';

export abstract class TranslateExternalOverrideToken {
  abstract get hasOverrides(): boolean | null;
  abstract getOverride(lang: string): Promise<IncomingTranslation>;
}
@Injectable({ providedIn: 'root' })
export class TranslateExternalOverrideService extends TranslateExternalOverrideToken {
  protected overrideKey = Override.i18n;
  protected sdk = inject(SDK);
  protected store = inject(STORE);

  protected readonly isOverridePresent = signal<boolean | null>(null);
  protected readonly cachedOverrides = signal<UnflattenedTranslationType>({});

  get hasOverrides() {
    return this.isOverridePresent();
  }

  /** Returns overrides from the middleware store, if present.
   * If no overrides are present or the current language is not
   * present or it is malformed, then an empty object is returned. */
  async getOverride(lang: string): Promise<IncomingTranslation> {
    if (this.hasOverrides === null) {
      this.isOverridePresent.set(
        await this.sdk.isOverridePresent(this.overrideKey),
      );
    }
    const empty = {} as IncomingTranslation;

    if (this.hasOverrides === false) return empty;

    const cache = this.cachedOverrides()[lang];
    if (cache) return cache;
    try {
      const translations = await this.store.get(
        'tenant',
        `${this.overrideKey}:${lang}`,
      );
      if (!isObject(translations)) {
        throw new TypeError(
          `Incorrect return type, expected translation object, got: ${JSON.stringify(translations)}`,
        );
      }

      this.cachedOverrides.update((prev) => ({
        ...prev,
        [lang]: translations,
      }));
      return translations;
    } catch (err) {
      console.warn(
        'An error occured while fetching translation overrides:',
        err,
      );
      return empty;
    }
  }
}

export class MockTranslateExternalOverrideService extends TranslateExternalOverrideToken {
  get hasOverrides(): boolean | null {
    return false;
  }
  getOverride(_lang: string): Promise<IncomingTranslation> {
    return Promise.resolve({});
  }
}
